import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private isLoggingOut = false; // Prevent multiple logout calls

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Check if this is a Cloudflare block (403 with specific indicators)
        // Don't treat Cloudflare blocks as auth errors
        if (this.isCloudflareBlock(error)) {
          console.warn('🛡️ Cloudflare security block detected');
          return throwError(() => error);
        }

        // Record-level access denied from passport endpoints (non-Cloudflare 403)
        // Toast is NOT shown here -- the calling component handles it to avoid duplicates
        if (error.status === 403 && this.isPassportAccessDenied(error)) {
          console.warn('🚫 Passport record-level access denied');
          return throwError(() => error);
        }

        // Check if it's a 401 Unauthorized error
        if (error.status === 401 && !this.isLoggingOut) {
          // Check if we are using the mock token - if so, don't logout
          if (localStorage.getItem('accessToken') === 'mock-token-for-development') {
            console.warn('🔒 401 Unauthorized ignored for mock token');
            return throwError(() => error);
          }

          console.warn('🔒 401 Unauthorized error detected - token may be expired');
          
          // Check if this is not an auth endpoint request (to avoid infinite loops)
          // Exclude: login, sso, token, refresh, and me endpoints
          if (!req.url.includes('/api/v1/auth/login') && 
              !req.url.includes('/api/v1/auth/sso') && 
              !req.url.includes('/api/v1/auth/token') &&
              !req.url.includes('/api/v1/auth/refresh') &&
              !req.url.includes('/api/v1/auth/me')) {
            this.isLoggingOut = true;
            
            // Logout the user and clear all data
            void this.authService.logout('Session expired. Please login again.').finally(() => {
              this.isLoggingOut = false;
            });
          }
        }
        
        // Re-throw the error so components can still handle it if needed
        return throwError(() => error);
      })
    );
  }

  /**
   * Detects if an error is from Cloudflare's security system.
   */
  private isCloudflareBlock(error: HttpErrorResponse): boolean {
    if (error.status !== 403) {
      return false;
    }

    // Check for Cloudflare-specific error code
    const errorObj = error.error;
    if (errorObj?.code === 'CLOUDFLARE_BLOCK') {
      return true;
    }

    // Check response headers for Cloudflare indicators
    if (error.headers?.has('cf-ray') || error.headers?.has('cf-cache-status')) {
      return true;
    }

    // Check response body for Cloudflare indicators
    const errorBody = typeof error.error === 'string' ? error.error : JSON.stringify(error.error ?? '');
    const cloudflareIndicators = ['cloudflare', 'cf-ray', 'ray id', 'security service', 'you have been blocked'];
    
    return cloudflareIndicators.some(indicator => errorBody?.toLowerCase().includes(indicator));
  }

  /**
   * Detects if a 403 is an application-level passport access denial (not Cloudflare).
   */
  private isPassportAccessDenied(error: HttpErrorResponse): boolean {
    if (error.status !== 403) return false;
    const errorObj = error.error;
    return errorObj?.error === 'access_denied' && (error.url?.includes('/api/v1/passport') ?? false);
  }
}

